import { useRef } from "react";
import * as XLSX from "xlsx";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { CSV_HEADERS, CSV_TEMPLATE_ROWS } from "../lib/samples";
import { Upload, Download, FileSpreadsheet, Trash2, Lock, AlertCircle, CheckCircle2 } from "lucide-react";

const FREE_ROW_LIMIT = 5;

const pick = (row, keys) => {
  for (const k of Object.keys(row)) {
    const nk = k.toLowerCase().trim();
    if (keys.some((x) => nk.includes(x))) return String(row[k] ?? "").trim();
  }
  return "";
};

export function BulkPanel({ rows, setRows, activeIndex, setActiveIndex, isPro }) {
  const inputRef = useRef(null);
  const invalidCount = rows.filter((r) => !(r.address && r.phone)).length;

  const downloadTemplate = () => {
    const ws = XLSX.utils.aoa_to_sheet([CSV_HEADERS, ...CSV_TEMPLATE_ROWS]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "template-resi-resicetak.xlsx");
    toast.success("Template diunduh");
  };

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      if (!json.length) return toast.error("File kosong atau format salah");
      let mapped = json.map((r) => ({
        recipient_name: pick(r, ["nama", "penerima", "name"]),
        phone: pick(r, ["hp", "telp", "phone", "wa"]),
        address: pick(r, ["alamat", "address"]),
        courier: pick(r, ["ekspedisi", "kurir", "courier"]),
        items: pick(r, ["barang", "isi", "item", "pesanan produk"]),
        notes: pick(r, ["catatan", "note"]),
        code: pick(r, ["kode", "resi", "invoice", "order"]),
        cod: pick(r, ["cod"]).replace(/[^\d]/g, ""),
      }));
      const limit = isPro ? 100 : FREE_ROW_LIMIT;
      let trimmed = false;
      if (mapped.length > limit) { mapped = mapped.slice(0, limit); trimmed = true; }
      setRows(mapped);
      setActiveIndex(0);
      toast.success(`${mapped.length} baris dimuat`);
      if (trimmed) {
        toast.warning(isPro ? "Maksimal 100 baris per batch" : `Akun gratis dibatasi ${FREE_ROW_LIMIT} baris. Upgrade Pro hingga 100 baris.`);
      }
    } catch {
      toast.error("Gagal membaca file. Pastikan format .xlsx atau .csv");
    }
  };

  return (
    <Card className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-bold text-slate-900">
          <FileSpreadsheet size={18} className="text-sky-600" /> Impor Massal (Excel / CSV)
        </div>
        <Button data-testid="btn-download-csv-template" variant="outline" size="sm" onClick={downloadTemplate} className="gap-1.5 text-xs">
          <Download size={14} /> Template
        </Button>
      </div>

      <div
        data-testid="dropzone-csv-upload"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}
        className="border-2 border-dashed border-slate-300 rounded-xl p-8 text-center cursor-pointer hover:border-sky-500 hover:bg-sky-50/50 transition-colors"
      >
        <Upload size={28} className="mx-auto text-slate-400 mb-2" />
        <div className="text-sm font-medium text-slate-700">Klik atau seret file ke sini</div>
        <div className="text-xs text-slate-400 mt-1">
          Format .xlsx / .csv • {isPro ? "hingga 100 baris" : `gratis maks ${FREE_ROW_LIMIT} baris`}
          {!isPro && <Lock size={11} className="inline ml-1 text-orange-500" />}
        </div>
        <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" className="hidden"
          data-testid="csv-file-input"
          onChange={(e) => handleFile(e.target.files[0])} />
      </div>

      {rows.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono">{rows.length - invalidCount} siap</Badge>
              {invalidCount > 0 && (
                <Badge data-testid="bulk-invalid-badge" className="bg-amber-100 text-amber-700 hover:bg-amber-100 font-mono">
                  {invalidCount} belum lengkap
                </Badge>
              )}
            </div>
            <Button variant="ghost" size="sm" className="text-red-500 text-xs" onClick={() => { setRows([]); setActiveIndex(0); }}>
              <Trash2 size={14} className="mr-1" /> Hapus semua
            </Button>
          </div>
          <div data-testid="batch-table-container" className="max-h-64 overflow-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-xs text-slate-500">
                  <th className="p-2">#</th><th className="p-2">Penerima</th><th className="p-2">HP</th><th className="p-2">Kurir</th><th className="p-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const invalid = !(r.address && r.phone);
                  return (
                    <tr key={i} data-testid={`batch-row-${i}`}
                      onClick={() => setActiveIndex(i)}
                      className={`cursor-pointer border-t border-slate-100 ${invalid ? "bg-amber-50" : activeIndex === i ? "bg-sky-50" : "hover:bg-slate-50"}`}>
                      <td className="p-2 text-slate-400">{i + 1}</td>
                      <td className="p-2 font-medium truncate max-w-[140px]">{r.recipient_name || "-"}</td>
                      <td className="p-2 text-slate-500">{r.phone || "-"}</td>
                      <td className="p-2 text-slate-500">{r.courier || "-"}</td>
                      <td className="p-2">
                        {invalid ? (
                          <span className="inline-flex items-center gap-1 text-amber-600 text-xs font-semibold" data-testid={`row-invalid-${i}`}>
                            <AlertCircle size={13} /> {!r.address && !r.phone ? "Alamat & HP" : !r.address ? "Alamat" : "HP"}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-emerald-600 text-xs"><CheckCircle2 size={13} /> OK</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400">
            Klik baris untuk preview. {invalidCount > 0 ? "Baris oranye (Alamat/HP kosong) otomatis dilewati saat cetak." : "Tombol cetak/PDF memproses semua label."}
          </p>
        </div>
      )}
    </Card>
  );
}
